import React, { useState, useEffect, useRef, useCallback, Component } from 'react';
import { Clock, Play, Pause, Square, X, Link as LinkIcon, Search, ChevronDown, Trash2, Plus, Pencil, Check } from 'lucide-react';
import { timeTrackingService, caseService } from '../../services/api';

// Error boundary so the timer can never crash the Navbar
class TimerErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

const formatTimerDisplay = (totalSeconds) => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const calcDisplaySeconds = (timer) => {
  let secs = timer.accumulated_seconds || 0;
  if (timer.is_running && timer.started_at) {
    secs += Math.floor((Date.now() - new Date(timer.started_at).getTime()) / 1000);
  }
  return secs;
};

const GlobalTimerWidget = ({ externalTimers, refetchNavbarStatus }) => {
  const [timers, setTimers] = useState([]);
  const [displaySeconds, setDisplaySeconds] = useState(0); // for the running timer
  const [showTimerList, setShowTimerList] = useState(false);
  const [showLinkPrompt, setShowLinkPrompt] = useState(false);
  const [promptMode, setPromptMode] = useState(null); // 'pause' or 'stop'
  const [promptTimerId, setPromptTimerId] = useState(null);
  const [cases, setCases] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCaseId, setSelectedCaseId] = useState('');
  const [selectedCaseLabel, setSelectedCaseLabel] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [confirmDiscardId, setConfirmDiscardId] = useState(null);
  const [editingNameId, setEditingNameId] = useState(null);
  const [editingNameValue, setEditingNameValue] = useState('');
  const intervalRef = useRef(null);
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const listRef = useRef(null);
  const nameInputRef = useRef(null);

  const runningTimer = timers.find(t => t.is_running);
  const pausedTimers = timers.filter(t => !t.is_running);

  const filteredCases = searchQuery.length > 0
    ? cases.filter(c => {
        const q = searchQuery.toLowerCase();
        return (c.title && c.title.toLowerCase().includes(q)) ||
               (c.case_number && c.case_number.toLowerCase().includes(q));
      })
    : cases;

  const startTick = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setDisplaySeconds(prev => prev + 1);
    }, 1000);
  }, []);

  const stopTick = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
      if (listRef.current && !listRef.current.contains(e.target)) {
        setShowTimerList(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Refresh local timers + shared navbar status after mutations
  const refreshTimers = useCallback(async () => {
    const allRes = await timeTrackingService.getTimers();
    const data = allRes.data || [];
    setTimers(data);
    if (refetchNavbarStatus) refetchNavbarStatus();
    return data;
  }, [refetchNavbarStatus]);

  // Sync timers from shared navbar status (no local polling)
  useEffect(() => {
    if (showLinkPrompt || !externalTimers) return;
    const data = externalTimers;
    setTimers(data);
    const running = data.find(t => t.is_running);
    if (running) {
      setDisplaySeconds(calcDisplaySeconds(running));
      startTick();
    } else {
      stopTick();
      if (data.length > 0) {
        setDisplaySeconds(data[0].accumulated_seconds || 0);
      } else {
        setDisplaySeconds(0);
      }
    }
  }, [externalTimers, startTick, stopTick, showLinkPrompt]);

  const handleStartTimer = async () => {
    try {
      await timeTrackingService.startTimer({});
      // Refresh all timers (start auto-pauses any running timer server-side)
      await refreshTimers();
      setDisplaySeconds(0);
      startTick();
      setShowTimerList(false);
    } catch { /* ignore */ }
  };

  const openLinkPrompt = (mode, timerId) => {
    setPromptMode(mode);
    setPromptTimerId(timerId);
    setSearchQuery('');
    setSelectedCaseId('');
    setSelectedCaseLabel('');
    setShowDropdown(false);
    setShowTimerList(false);
    fetchCases();
    setShowLinkPrompt(true);
    setTimeout(() => searchInputRef.current?.focus(), 100);
  };

  const handlePause = async (timerId) => {
    try {
      await timeTrackingService.pauseTimer(timerId);
      const data = await refreshTimers();
      stopTick();
      const paused = data.find(t => t.id === timerId);
      if (paused && !paused.case_id) {
        openLinkPrompt('pause', timerId);
      }
    } catch { /* ignore */ }
  };

  const handleResume = async (timerId) => {
    try {
      setShowLinkPrompt(false);
      await timeTrackingService.resumeTimer(timerId);
      const data = await refreshTimers();
      const running = data.find(t => t.is_running);
      if (running) {
        setDisplaySeconds(calcDisplaySeconds(running));
        startTick();
      }
      setShowTimerList(false);
    } catch { /* ignore */ }
  };

  const handleStop = async (timerId) => {
    try {
      stopTick();
      const timerToStop = timers.find(t => t.id === timerId);
      // If no case linked, show prompt before saving
      if (timerToStop && !timerToStop.case_id) {
        // Pause first so the timer is preserved server-side
        if (timerToStop.is_running) {
          try { await timeTrackingService.pauseTimer(timerId); } catch { /* may already be paused */ }
        }
        openLinkPrompt('stop', timerId);
        return;
      }
      await timeTrackingService.stopTimer(timerId);
      setShowLinkPrompt(false);
      const data = await refreshTimers();
      const running = data.find(t => t.is_running);
      if (running) {
        setDisplaySeconds(calcDisplaySeconds(running));
        startTick();
      } else {
        setDisplaySeconds(0);
      }
    } catch { /* ignore */ }
  };

  const handleDiscardClick = (timerId) => {
    setConfirmDiscardId(timerId);
  };

  const handleDiscardConfirm = async () => {
    const timerId = confirmDiscardId;
    setConfirmDiscardId(null);
    try {
      await timeTrackingService.discardTimer(timerId);
      const data = await refreshTimers();
      const running = data.find(t => t.is_running);
      if (running) {
        setDisplaySeconds(calcDisplaySeconds(running));
        startTick();
      } else {
        stopTick();
        setDisplaySeconds(0);
      }
    } catch { /* ignore */ }
  };

  const handleDiscardCancel = () => {
    setConfirmDiscardId(null);
  };

  const handleStartEditName = (timer) => {
    setEditingNameId(timer.id);
    setEditingNameValue(timer.description || '');
    setTimeout(() => nameInputRef.current?.focus(), 50);
  };

  const handleSaveName = async (timerId) => {
    try {
      await timeTrackingService.updateTimer(timerId, { description: editingNameValue || null });
      await refreshTimers();
    } catch { /* ignore */ }
    setEditingNameId(null);
    setEditingNameValue('');
  };

  const handleCancelEditName = () => {
    setEditingNameId(null);
    setEditingNameValue('');
  };

  const fetchCases = async () => {
    try {
      const res = await caseService.getCases({ limit: 2000 });
      setCases(res.data?.cases || res.data || []);
    } catch { /* ignore */ }
  };

  const handleSelectCase = (c) => {
    setSelectedCaseId(c.id);
    setSelectedCaseLabel(`${c.case_number ? c.case_number + ' - ' : ''}${c.title}`);
    setSearchQuery(`${c.case_number ? c.case_number + ' - ' : ''}${c.title}`);
    setShowDropdown(false);
  };

  const handleConfirm = async () => {
    try {
      if (selectedCaseId && promptTimerId) {
        await timeTrackingService.updateTimer(promptTimerId, { case_id: selectedCaseId });
      }
      if (promptMode === 'stop' && promptTimerId) {
        await timeTrackingService.stopTimer(promptTimerId);
      }
      setShowLinkPrompt(false);
      setSelectedCaseId('');
      setSelectedCaseLabel('');
      setSearchQuery('');
      setPromptTimerId(null);
      // Refresh timers
      const data = await refreshTimers();
      const running = data.find(t => t.is_running);
      if (running) {
        setDisplaySeconds(calcDisplaySeconds(running));
        startTick();
      } else {
        stopTick();
        setDisplaySeconds(0);
      }
    } catch { /* ignore */ }
  };

  const handleSkip = async () => {
    try {
      if (promptMode === 'stop' && promptTimerId) {
        await timeTrackingService.stopTimer(promptTimerId);
      }
      setShowLinkPrompt(false);
      setSelectedCaseId('');
      setSelectedCaseLabel('');
      setSearchQuery('');
      setPromptTimerId(null);
      const data = await refreshTimers();
      const running = data.find(t => t.is_running);
      if (running) {
        setDisplaySeconds(calcDisplaySeconds(running));
        startTick();
      } else {
        stopTick();
        setDisplaySeconds(0);
      }
    } catch { /* ignore */ }
  };

  const handleDismissPrompt = () => {
    setShowLinkPrompt(false);
    setSelectedCaseId('');
    setSelectedCaseLabel('');
    setSearchQuery('');
    setPromptTimerId(null);
  };

  // No timers at all — show start button
  if (timers.length === 0 && !showLinkPrompt) {
    return (
      <button
        onClick={handleStartTimer}
        className="flex items-center space-x-1 text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100"
        title="Start timer"
      >
        <Clock className="h-4 w-4" />
      </button>
    );
  }

  return (
    <div className="relative" ref={listRef}>
      <div className="flex items-center space-x-2 bg-gray-100 rounded-lg px-3 py-1.5">
        {/* Timer display — show running timer or first paused */}
        <div className={`font-mono text-sm font-semibold ${runningTimer ? 'text-green-700' : 'text-yellow-700'}`}>
          {formatTimerDisplay(runningTimer ? displaySeconds : (timers[0]?.accumulated_seconds || 0))}
        </div>

        {/* Case label */}
        {(runningTimer || timers[0])?.case_title && (
          <span className="text-xs text-gray-500 max-w-[120px] truncate" title={(runningTimer || timers[0]).case_title}>
            {(runningTimer || timers[0]).case_title}
          </span>
        )}

        {/* Controls for the primary timer */}
        {runningTimer ? (
          <button onClick={() => handlePause(runningTimer.id)} className="text-yellow-600 hover:text-yellow-800 p-0.5" title="Pause">
            <Pause className="h-4 w-4" />
          </button>
        ) : timers[0] && (
          <button onClick={() => handleResume(timers[0].id)} className="text-green-600 hover:text-green-800 p-0.5" title="Resume">
            <Play className="h-4 w-4" />
          </button>
        )}
        <button
          onClick={() => handleStop(runningTimer?.id || timers[0]?.id)}
          className="text-red-600 hover:text-red-800 p-0.5"
          title="Stop & save"
        >
          <Square className="h-3.5 w-3.5" />
        </button>

        {/* Timer list toggle — show if there are multiple timers or to start a new one */}
        <button
          onClick={() => setShowTimerList(!showTimerList)}
          className="text-gray-500 hover:text-gray-700 p-0.5 relative"
          title="All timers"
        >
          <ChevronDown className="h-3.5 w-3.5" />
          {pausedTimers.length > 0 && (
            <span className="absolute -top-1.5 -right-1.5 bg-yellow-500 text-white text-[10px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center">
              {pausedTimers.length}
            </span>
          )}
        </button>
      </div>

      {/* Timer List Dropdown */}
      {showTimerList && (
        <div className="absolute top-full right-0 mt-2 w-72 bg-white text-gray-900 rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="p-3 border-b border-gray-100 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Timers ({timers.length})</span>
            <button
              onClick={handleStartTimer}
              className="text-xs text-yellow-600 hover:text-yellow-800 flex items-center"
              title="Start new timer"
            >
              <Plus className="h-3 w-3 mr-0.5" />
              New
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {timers.map(t => (
              <div
                key={t.id}
                className={`px-3 py-2 border-b border-gray-50 last:border-0 ${
                  t.is_running ? 'bg-green-50' : 'hover:bg-gray-50'
                }`}
              >
                {confirmDiscardId === t.id ? (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">Discard this timer?</span>
                    <div className="flex items-center space-x-1 ml-2">
                      <button onClick={handleDiscardConfirm} className="text-red-600 hover:text-red-800 text-xs font-medium px-2 py-0.5 rounded hover:bg-red-50">
                        Discard
                      </button>
                      <button onClick={handleDiscardCancel} className="text-gray-500 hover:text-gray-700 text-xs px-2 py-0.5 rounded hover:bg-gray-100">
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <span className={`font-mono text-sm font-semibold ${t.is_running ? 'text-green-700' : 'text-yellow-700'}`}>
                          {formatTimerDisplay(t.is_running ? displaySeconds : (t.accumulated_seconds || 0))}
                        </span>
                        {t.is_running && (
                          <span className="text-[10px] bg-green-100 text-green-700 px-1 rounded font-medium">RUNNING</span>
                        )}
                      </div>
                      {editingNameId === t.id ? (
                        <div className="flex items-center space-x-1 mt-0.5">
                          <input
                            ref={nameInputRef}
                            type="text"
                            value={editingNameValue}
                            onChange={(e) => setEditingNameValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveName(t.id); if (e.key === 'Escape') handleCancelEditName(); }}
                            placeholder="Timer name..."
                            className="text-xs border border-gray-300 rounded px-1.5 py-0.5 w-full focus:outline-none focus:ring-1 focus:ring-yellow-500"
                          />
                          <button onClick={() => handleSaveName(t.id)} className="text-green-600 hover:text-green-800 p-0.5" title="Save name">
                            <Check className="h-3 w-3" />
                          </button>
                          <button onClick={handleCancelEditName} className="text-gray-400 hover:text-gray-600 p-0.5" title="Cancel">
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center mt-0.5 group/name">
                          {t.case_title ? (
                            <span className="text-xs text-gray-500 truncate">{t.case_title}</span>
                          ) : t.description ? (
                            <span className="text-xs text-gray-500 truncate">{t.description}</span>
                          ) : (
                            <span className="text-xs text-gray-400 italic">No name</span>
                          )}
                          <button
                            onClick={() => handleStartEditName(t)}
                            className="text-gray-300 hover:text-gray-500 p-0.5 ml-1 opacity-0 group-hover/name:opacity-100"
                            title="Name this timer"
                          >
                            <Pencil className="h-2.5 w-2.5" />
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-1 ml-2">
                      {t.is_running ? (
                        <button onClick={() => handlePause(t.id)} className="text-yellow-600 hover:text-yellow-800 p-1" title="Pause">
                          <Pause className="h-3.5 w-3.5" />
                        </button>
                      ) : (
                        <button onClick={() => handleResume(t.id)} className="text-green-600 hover:text-green-800 p-1" title="Resume (pauses other)">
                          <Play className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button onClick={() => handleStop(t.id)} className="text-red-600 hover:text-red-800 p-1" title="Stop & save">
                        <Square className="h-3 w-3" />
                      </button>
                      <button onClick={() => handleDiscardClick(t.id)} className="text-gray-400 hover:text-gray-600 p-1" title="Discard">
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Link to Case Prompt (dropdown) */}
      {showLinkPrompt && (
        <div className="absolute top-full right-0 mt-2 w-80 bg-white text-gray-900 rounded-lg shadow-lg border border-gray-200 p-4 z-50">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center text-sm font-medium text-gray-700">
              <LinkIcon className="h-4 w-4 mr-1" />
              {promptMode === 'stop' ? 'Save time to a case?' : 'Link to a case?'}
            </div>
            <button onClick={handleDismissPrompt} className="text-gray-400 hover:text-gray-600">
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="text-xs text-gray-500 mb-3">
            {promptMode === 'stop'
              ? 'Assign this time entry to a case, or save it unlinked.'
              : 'Optionally assign this time to a case. You can skip this.'}
          </p>

          {/* Searchable case selector */}
          <div className="relative mb-2" ref={dropdownRef}>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowDropdown(true);
                  if (selectedCaseId && e.target.value !== selectedCaseLabel) {
                    setSelectedCaseId('');
                    setSelectedCaseLabel('');
                  }
                }}
                onFocus={() => { if (searchQuery && !selectedCaseId) setShowDropdown(true); }}
                placeholder="Search cases..."
                className="w-full border border-gray-300 rounded-md pl-8 pr-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div>
            {showDropdown && filteredCases.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-36 overflow-y-auto z-[60]">
                {filteredCases.slice(0, 50).map(c => (
                    <button
                      key={c.id}
                      onClick={() => handleSelectCase(c)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-yellow-50 border-b border-gray-50 last:border-0 ${
                        selectedCaseId === c.id ? 'bg-yellow-50 font-medium' : ''
                      }`}
                    >
                      {c.case_number && (
                        <span className="text-gray-500 mr-1">{c.case_number} -</span>
                      )}
                      <span className="text-gray-800">{c.title}</span>
                    </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex space-x-2">
            <button
              onClick={handleConfirm}
              className={`flex-1 text-white px-3 py-1.5 rounded text-sm ${
                selectedCaseId
                  ? 'bg-yellow-500 hover:bg-yellow-600'
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
              disabled={!selectedCaseId}
            >
              {promptMode === 'stop' ? 'Save' : 'Link'}
            </button>
            <button
              onClick={handleSkip}
              className="flex-1 text-gray-600 px-3 py-1.5 rounded text-sm hover:bg-gray-100 border border-gray-200"
            >
              {promptMode === 'stop' ? 'Save without case' : 'Skip'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const SafeGlobalTimerWidget = () => (
  <TimerErrorBoundary>
    <GlobalTimerWidget />
  </TimerErrorBoundary>
);

export default SafeGlobalTimerWidget;
